"use client";

import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { useTriggerAction, type BlokAction } from "@/lib/action-link";
import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";
import YouTubePlayer from "@/components/video/youtube-player";

type VideoFeatureBlok = SbBlokData & {
  title?: string;
  body?: string;
  youtube_url?: string;
  button_label?: string;
  button_link?: StoryblokLink;
  button_action?: BlokAction | "";
  reverse_order?: boolean;
  bg_color?: unknown;
};

function extractYouTubeId(url: string | undefined): string | null {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

function PlaceholderIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 68 48" className={className} aria-hidden>
      <path
        fill="#ff0000"
        d="M66.5 7.5c-.8-3-3.2-5.4-6.2-6.2C54.9 0 34 0 34 0S13.1 0 7.7 1.3C4.7 2.1 2.3 4.5 1.5 7.5 0 13 0 24 0 24s0 11 1.5 16.5c.8 3 3.2 5.4 6.2 6.2C13.1 48 34 48 34 48s20.9 0 26.3-1.3c3-.8 5.4-3.2 6.2-6.2C68 35 68 24 68 24s0-11-1.5-16.5z"
      />
      <path fill="#fff" d="M27 34V14l17 10z" />
    </svg>
  );
}

export default function VideoFeature({ blok }: { blok: VideoFeatureBlok }) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const { bg, onDark } = resolveBg(blok.bg_color, "white");
  const id = extractYouTubeId(blok.youtube_url);

  const link = resolveLink(blok.button_link, region);
  const triggerAction = useTriggerAction();
  const action = blok.button_action || null;
  const showButton = Boolean(blok.button_label && (action || link.href !== "#"));

  const buttonClass = `mt-2 inline-flex h-14 w-fit items-center justify-center rounded-[8px] px-7 text-body font-bold transition-colors ${
    onDark
      ? "bg-white text-purple-05 hover:bg-purple-01"
      : "bg-purple-03 text-white hover:bg-purple-04"
  }`;

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(2.5rem,5vw,5rem)]">
        <div
          className={`flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-[clamp(2.5rem,6vw,6.5rem)] ${
            blok.reverse_order ? "lg:flex-row-reverse" : ""
          }`}
        >
          <div className="flex flex-col gap-5 lg:flex-1">
            {blok.title && (
              <h2
                className={`text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold ${
                  onDark ? "text-white" : "text-purple-05"
                }`}
              >
                {blok.title}
              </h2>
            )}
            {blok.body && (
              <p
                className={`text-body leading-[1.56] ${
                  onDark ? "text-white/85" : "text-[#595959]"
                }`}
              >
                {blok.body}
              </p>
            )}
            {showButton &&
              (action ? (
                <button
                  type="button"
                  onClick={(e) => triggerAction(action, e)}
                  className={`cursor-pointer ${buttonClass}`}
                >
                  {blok.button_label}
                </button>
              ) : (
                <a
                  href={link.href}
                  {...(link.target ? { target: link.target } : {})}
                  className={buttonClass}
                >
                  {blok.button_label}
                </a>
              ))}
          </div>

          <div className="w-full lg:flex-1">
            <div className="bg-dusk-02 relative aspect-video w-full overflow-hidden rounded-[16px] lg:rounded-[24px]">
              {id ? (
                <YouTubePlayer videoId={id} title={blok.title} />
              ) : (
                <span className="absolute inset-0 flex items-center justify-center">
                  <PlaceholderIcon className="h-[44px] w-[66px] opacity-70" />
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
